
import { Link } from 'react-router-dom';

export default function SuccessFactorsPage() {
  const successConditions = [
    {
      title: 'チーム規模が大きいほど効果が高い（特に10名以上）',
      description: 'チームビルディングの効果は、チーム規模が大きいほど顕著に現れる。小規模チームでは自然に形成される相互作用も、大規模チームでは意図的な設計が必要になる。',
      icon: 'ri-team-line',
      practices: [
        '10名以上のチームでは、役割の明確化を最優先する',
        'サブチームに分ける場合も、全体の目標を共有する',
        '大規模チームほど、定期的な振り返りが重要',
        '情報共有の仕組みを明確にする'
      ]
    },
    {
      title: '講義型より能動的学習（対話・振り返り）',
      description: '一方的な講義や研修よりも、メンバー同士の対話や振り返りの方が効果が高い。能動的に参加することで、学びが定着し、行動変容につながる。',
      icon: 'ri-discuss-line',
      practices: [
        'ミーティングでは全員が発言する機会をつくる',
        '講義型研修ではなく、ワークショップ形式を採用する',
        '振り返りの時間を必ず設ける',
        'メンバー同士の対話を促進する'
      ]
    },
    {
      title: '単発施策ではなく継続設計',
      description: 'チームビルディングは一度やれば終わりではなく、継続的に取り組むことで効果が持続する。単発のイベントではなく、日常業務に組み込むことが重要。',
      icon: 'ri-refresh-line',
      practices: [
        '週次・月次の振り返りを習慣化する',
        '1on1を定期的に実施する',
        'チームビルディングを日常業務に組み込む',
        '改善のPDCAを回し続ける'
      ]
    },
    {
      title: '定期的なデブリーフィング（振り返り）',
      description: '振り返り導入だけで成果20〜25％向上のエビデンスあり。定期的に「うまくいったこと」「改善すべきこと」を共有することで、チームの学習速度が上がる。',
      icon: 'ri-feedback-line',
      practices: [
        '週次で15分の振り返りを実施する',
        '「うまくいったこと」から始める',
        '「改善すべきこと」を具体的に話し合う',
        '次週のアクションを決める'
      ]
    }
  ];

  const commonMistakes = [
    {
      title: '人間関係改善から始める',
      problem: '「仲良くなれば成果が出る」という誤解',
      solution: '役割の明確化から始める。人間関係は結果として良くなる。',
      icon: 'ri-user-smile-line'
    },
    {
      title: '研修だけで解決しようとする',
      problem: '単発の研修では行動変容は起きない',
      solution: '日常業務に組み込み、継続的に取り組む。',
      icon: 'ri-book-line'
    },
    {
      title: '人の入れ替えで対処する',
      problem: '「問題のある人」を排除しても、構造が変わらなければ同じ問題が起きる',
      solution: '構造を変える。役割・目標・プロセスを見直す。',
      icon: 'ri-user-unfollow-line'
    },
    {
      title: '問題を個人の資質に帰属させる',
      problem: '「やる気がない」「能力が低い」と個人を責める',
      solution: '環境や構造の問題として捉え、システムを改善する。',
      icon: 'ri-user-search-line'
    }
  ];

  const pharmacyTranslations = [
    {
      title: '朝礼・ミーティングの設計',
      description: '連絡事項の共有だけでなく、役割確認・優先順位の共有・問題の早期発見の場として機能させる',
      icon: 'ri-calendar-check-line',
      practices: [
        '今日の役割分担を確認する',
        '優先順位を共有する',
        '困っていることを共有する',
        '昨日の振り返りを簡単に行う'
      ]
    },
    {
      title: '1on1の目的転換（評価→学習）',
      description: '評価面談ではなく、学習・成長のための対話の場として再設計する',
      icon: 'ri-user-voice-line',
      practices: [
        '「最近うまくいったことは？」から始める',
        '「困っていることは？」を聞く',
        '「どうすればもっと働きやすくなるか」を一緒に考える',
        '評価ではなく、成長支援の場として位置づける'
      ]
    },
    {
      title: 'ヒヤリハット共有の再設計',
      description: '個人の責任追及ではなく、システム改善の機会として扱う文化をつくる',
      icon: 'ri-alert-line',
      practices: [
        '「誰が悪いか」ではなく「どうすれば防げるか」を考える',
        'ヒヤリハットを報告した人に感謝する',
        '改善策を具体的に決める',
        '改善の効果を検証する'
      ]
    },
    {
      title: '在宅・地域連携の役割明確化',
      description: '誰が何をどこまで担うか、情報共有のルール、判断基準を明確にする',
      icon: 'ri-home-heart-line',
      practices: [
        '在宅訪問の役割分担を明確にする',
        '情報共有のルールを決める',
        '判断基準を共有する',
        '連携先との役割分担を明確にする'
      ]
    },
    {
      title: '属人業務の構造化',
      description: '特定の人しかできない業務を洗い出し、手順化・共有化する',
      icon: 'ri-file-list-3-line',
      practices: [
        '属人業務をリストアップする',
        '手順書を作成する',
        '複数人で対応できるようにする',
        '定期的に見直す'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-rose-100">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-rose-600 transition-colors duration-200">ホーム</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/team-building" className="hover:text-rose-600 transition-colors duration-200">チームビルディング</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-rose-600 font-medium">成果最大化・誤解・薬局翻訳</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl mb-6 shadow-lg">
              <i className="ri-line-chart-line text-3xl text-white"></i>
            </div>
            <h1 className="text-4xl font-bold mb-6 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              Ⅴ・Ⅵ・Ⅶ．実践のポイント
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              成果最大化の条件・よくある誤解・薬局マネジメントへの翻訳
            </p>
          </div>
        </div>
      </section>

      {/* Success Conditions */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Ⅴ．成果を最大化する条件</h2>
            <div className="space-y-8">
              {successConditions.map((condition, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-rose-100">
                  <div className="flex items-start gap-6">
                    <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl shadow-md flex-shrink-0">
                      <i className={`${condition.icon} text-2xl text-white`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{condition.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-6">{condition.description}</p>
                      <div className="bg-rose-50 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-rose-900 mb-4 flex items-center gap-2">
                          <i className="ri-checkbox-circle-line"></i>
                          実践のポイント
                        </h4>
                        <ul className="space-y-3">
                          {condition.practices.map((practice, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-gray-700">
                              <i className="ri-arrow-right-s-line text-rose-600 mt-1 flex-shrink-0"></i>
                              <span>{practice}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Ⅵ．よくある誤解・失敗パターン</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {commonMistakes.map((mistake, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-rose-100">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg mb-4">
                    <i className={`${mistake.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{mistake.title}</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-close-circle-line text-red-600"></i>
                        <span className="font-bold text-red-900">問題</span>
                      </div>
                      <p className="text-sm text-gray-700 pl-6">{mistake.problem}</p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <i className="ri-checkbox-circle-line text-green-600"></i>
                        <span className="font-bold text-green-900">解決策</span>
                      </div>
                      <p className="text-sm text-gray-700 pl-6">{mistake.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pharmacy Translations */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Ⅶ．薬局マネジメントへの翻訳</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pharmacyTranslations.map((translation, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-rose-100 hover:shadow-md transition-all duration-300">
                  <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-rose-500 to-pink-500 rounded-lg mb-4">
                    <i className={`${translation.icon} text-xl text-white`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">{translation.title}</h3>
                  <p className="text-sm text-gray-700 mb-4">{translation.description}</p>
                  <div className="space-y-2">
                    {translation.practices.map((practice, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                        <i className="ri-checkbox-line text-rose-600 mt-0.5 flex-shrink-0"></i>
                        <span>{practice}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Action Section */}
      <section className="py-16 bg-white/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-8 text-white shadow-xl">
              <div className="flex items-start gap-6">
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl flex-shrink-0">
                  <i className="ri-lightbulb-flash-line text-3xl"></i>
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-4">明日の一手</h2>
                  <p className="text-lg leading-relaxed mb-6">
                    まずは「定期的な振り返り」から始めましょう。次回のミーティングの最後に15分だけ、「今週うまくいったこと」「来週改善したいこと」を全員で共有してみてください。振り返りを習慣化するだけで、チームの成果は20〜25％向上します。
                  </p>
                  <div className="flex items-center gap-2 text-sm bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg inline-flex whitespace-nowrap">
                    <i className="ri-time-line"></i>
                    <span>所要時間：15分</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-between items-center">
              <Link
                to="/team-building/leader-role"
                className="flex items-center gap-2 text-rose-600 hover:text-rose-700 font-medium transition-colors duration-200"
              >
                <i className="ri-arrow-left-line"></i>
                <span>前へ：リーダーの役割</span>
              </Link>
              <Link
                to="/team-building"
                className="flex items-center gap-2 bg-gradient-to-r from-rose-500 to-pink-500 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 whitespace-nowrap"
              >
                <span>一覧に戻る</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
